/**
 * The screens part of the console API (design 18): global search, the merged desk across companies,
 * and phone line operations. `ConsoleApi` extends it and `ConsoleClient` mixes `screenMethods` in.
 */
import type { LineCallView, LineHealthView, MergedDeskView, PhoneLineView, SearchKind, SearchResult, SendQueueView, StaffPhoneView } from '@takeit4me/shared'
import type { Http } from './http'
import { qs } from './http'

export interface SearchQuery { q: string; kinds?: SearchKind[]; limit?: number }

export interface ScreensApi {
  /** Top-bar search across people, organisations, records, conversations, tasks and knowledge (by role, masked). */
  search(q: SearchQuery): Promise<SearchResult>
  /** Today's "All send queues · oldest item" row: what is waiting to go and how long the oldest has waited. */
  sendQueue(): Promise<SendQueueView>
  /** One desk queue across every company this sign-in has a desk seat in, with company badges. */
  mergedDesk(q?: { view?: 'team' | 'mine' | 'approvals'; status?: 'open' | 'resolved'; reason?: string; limit?: number }): Promise<MergedDeskView>
  /** Desk focus time (19-11): the running total for one opening of a desk item or conversation. */
  deskActivity(body: { session_id: string; item_kind: 'exception' | 'conversation'; item_id: string; active_ms: number }): Promise<{ active_ms: number }>
  /** A phone line: its latest health check, last test call, today's numbers and recent calls, and your phone. */
  phoneLine(channelId: string): Promise<PhoneLineView>
  /** Checks the line now (carrier, SIP trunk, OpenAI, routing, history). Admins. */
  checkLine(channelId: string): Promise<LineHealthView>
  lineCalls(channelId: string, limit?: number): Promise<{ items: LineCallView[] }>
  /** The line's greeting spoken in one voice (MP3). */
  voiceSample(channelId: string, voice: string): Promise<Blob>
  /** "Call me now": the line calls your verified phone. Admins. */
  testCall(channelId: string): Promise<{ call_id: string }>
  myPhone(): Promise<StaffPhoneView>
  sendPhoneCode(): Promise<StaffPhoneView>
  verifyPhone(code: string): Promise<StaffPhoneView>
}

const enc = encodeURIComponent

export function screenMethods(http: Http): ScreensApi {
  return {
    search: ({ q, kinds, limit }) => http.get<SearchResult>(`/search${qs({ q, kinds: kinds?.length ? kinds.join(',') : undefined, limit })}`),
    sendQueue: () => http.get<SendQueueView>('/today/send-queue'),
    mergedDesk: (q = {}) => http.get<MergedDeskView>(`/desk/all-companies${qs(q)}`),
    deskActivity: (body) => http.post<{ active_ms: number }>('/desk/activity', body),
    phoneLine: (id) => http.get<PhoneLineView>(`/channels/${enc(id)}/line`),
    checkLine: (id) => http.post<LineHealthView>(`/channels/${enc(id)}/check`),
    lineCalls: (id, limit) => http.get<{ items: LineCallView[] }>(`/channels/${enc(id)}/calls${qs({ limit })}`),
    voiceSample: async (id, voice) => (await http.raw('GET', `/channels/${enc(id)}/voice-sample${qs({ voice })}`)).blob(),
    testCall: (id) => http.post<{ call_id: string }>(`/channels/${enc(id)}/test-call`),
    myPhone: () => http.get<StaffPhoneView>('/me/phone'),
    sendPhoneCode: () => http.post<StaffPhoneView>('/me/phone/code'),
    verifyPhone: (code) => http.post<StaffPhoneView>('/me/phone/verify', { code }),
  }
}
